/**
 * yjs worker — message protocol shared between the Web Worker
 * (`yjsWorker.worker.ts`) and the main-thread client (`yjsWorkerClient.ts`).
 *
 * Mirrors the websocket-server's `src/yjs/yjsWorkerProtocol.js` exactly so the
 * mental model is identical across both runtimes. Keeping a separate copy
 * (instead of importing from the server module) avoids dragging Node-only
 * dependencies into the browser bundle.
 *
 * Wire format
 * ───────────
 *   Request  (main → worker):   { id: number, op: string, payload: object }
 *   Response (worker → main):   { id: number, ok: true, result: any }
 *                               | { id: number, ok: false, error: string }
 *
 * Uint8Arrays in payloads / results are transferred via Worker `transfer`
 * for zero-copy. After transfer, the sender's buffer is detached — the
 * client API documents this so callers know not to reuse passed-in buffers.
 *
 * Why this exists
 * ───────────────
 * `Y.encodeStateAsUpdate(doc)` and `Y.applyUpdate(doc, bytes)` block the
 * main thread for >1 s on a 64 MiB org-doc — the chandler-pattern UI freeze
 * (frozen inputs, dropped video frames, missed scroll events while sync
 * step 2 encodes/applies). Moving the work to a Web Worker lets the UI
 * stay responsive even while a huge doc is being processed.
 *
 * Reference: `/workspace/.cursor/plans/websocket_robustness_refactor_b886ced2.plan.md`
 */

export const OP_ENCODE_FROM_UPDATES = 'encodeFromUpdates' as const;
export const OP_MERGE_UPDATES = 'mergeUpdates' as const;
export const OP_ENCODE_FROM_STATE = 'encodeFromState' as const;
export const OP_APPLY_UPDATE_TO_STATE = 'applyUpdateToState' as const;
export const OP_PING = 'ping' as const;

export const KNOWN_OPS = [
	OP_ENCODE_FROM_UPDATES,
	OP_MERGE_UPDATES,
	OP_ENCODE_FROM_STATE,
	OP_APPLY_UPDATE_TO_STATE,
	OP_PING,
] as const;

export type WorkerOp = typeof KNOWN_OPS[number];

export interface WorkerRequest {
	id: number;
	op: WorkerOp;
	payload: unknown;
}

export interface WorkerOkResponse {
	id: number;
	ok: true;
	result: unknown;
}

export interface WorkerErrResponse {
	id: number;
	ok: false;
	error: string;
	stack?: string;
}

export type WorkerResponse = WorkerOkResponse | WorkerErrResponse;

// ── Per-op payload + result types ─────────────────────────────────────────────

export interface EncodeFromUpdatesPayload {
	updates: Uint8Array[];
}
export type EncodeFromUpdatesResult = Uint8Array;

export interface MergeUpdatesPayload {
	updates: Uint8Array[];
}
export type MergeUpdatesResult = Uint8Array;

export interface EncodeFromStatePayload {
	state: Uint8Array;
}
export type EncodeFromStateResult = Uint8Array;

/**
 * Apply an incoming update on top of an existing serialized state and return
 * the new state. The CLIENT-SIDE primitive that lets us move sync-step-2
 * apply off-thread without keeping a full Y.Doc in the worker.
 *
 * The main-thread call site looks like:
 *   const before = await getLastEncodedState(roomId);          // memoized
 *   const after  = await client.applyUpdateToState(before, msg);
 *   await setLastEncodedState(roomId, after);
 *   await sendUpdateToMainThreadDoc(msg);                       // tiny update; cheap
 *
 * The "applyUpdate to main-thread doc" still happens on main thread, but
 * that's a 100 KB-or-less update — orders of magnitude smaller than the
 * 64 MiB encoded state operation we're trying to avoid.
 */
export interface ApplyUpdateToStatePayload {
	state: Uint8Array;
	update: Uint8Array;
}
export type ApplyUpdateToStateResult = Uint8Array;

export type PingPayload = Record<string, never>;
export type PingResult = 'pong';
